import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Tooltip } from "@mui/material";
import cx from "classnames";
import React from "react";
import { toast } from "react-toastify";

import { ReactComponent as DeleteIcon } from "assets/delete-icon.svg";
import Guide from "components/Guide";
import { UserGuides, WINDOW_EVENTS } from "constants/constants";
import { Cutout } from "models/Cutout";
import { LayerCompact } from "models/Layer";
import { renameLayer } from "services/LayerService";
import { useClickHandler } from "utils/hooks";

import CutoutCard from "./CutoutCard";
import Layer from "./Layer";
import styles from "./Layers.module.scss";

const LAYER_NAME_PATTERN = /^(?=.{3,}$)(?![\s])[a-zA-Z0-9\s\-_]+$/;

interface LayerCascadeProps extends Omit<
  React.ComponentProps<typeof Layer>,
  "layer"
> {
  layer: LayerCompact;
  cutouts: Cutout[];
  isExpanded: boolean;
  onToggle: () => void;
  /**
   * Optional "Add more annotations" Guide anchored to the header's "+"
   * button. Computed by the parent based on cutout count + inference status;
   * auto-opens when present. Pass `null` to suppress.
   */
  addAnnotationTip?: {
    id: UserGuides;
    title: string;
    description: string;
  } | null;
}

const LayerCascade: React.FC<LayerCascadeProps> = ({
  layer,
  cutouts,
  isExpanded,
  onToggle,
  addAnnotationTip,
  ...layerProps
}) => {
  const {
    handleSingleClick,
    layerInputEl,
    layers,
    selectedLayer,
    setSelectedLayer,
    setLayers,
    setCutoutInProgress,
    setLayerAnchorEl,
    setShowLayerDeleteModal,
    showRenameLayerModal,
    setShowRenameLayerModal,
  } = layerProps;

  const handleHeaderClick = useClickHandler(handleSingleClick, () => {
    setSelectedLayer(layer);
    setShowRenameLayerModal?.(true);
  });
  const isRenaming = !!showRenameLayerModal && selectedLayer?.id === layer.id;
  const hasCutouts = cutouts.length > 0;
  const ghostCount = Math.min(cutouts.length, 2);
  const sortedCutouts = [...cutouts].sort((a, b) => {
    const aTime = a.updated ? new Date(a.updated).getTime() : a.id;
    const bTime = b.updated ? new Date(b.updated).getTime() : b.id;
    return bTime - aTime;
  });

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  const handleAddAnnotation = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Set the active layer synchronously *before* dispatching ANNOTATE —
    // `handleSingleClick` only navigates (URL change is async), so without
    // this the workspace would open with a stale `selectedLayer` and the
    // save path would create a new layer instead of appending to this one.
    setSelectedLayer(layer);
    handleSingleClick();
    window.dispatchEvent(new CustomEvent(WINDOW_EVENTS.ANNOTATE));
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLayerAnchorEl(null);
    setSelectedLayer(layer);
    setShowLayerDeleteModal(true);
  };

  const header = (
    <div
      className={styles.cascadeHeader}
      onClick={handleHeaderClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleHeaderClick();
        }
      }}
    >
      {isRenaming ? (
        <input
          type="text"
          className={styles.editLayerName}
          defaultValue={layer.name}
          ref={layerInputEl}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          onBlur={(e) => {
            if (e.relatedTarget?.classList.contains("MuiMenu-paper")) return;
            const newName = e.currentTarget.value.trim();
            if (!newName.length || !LAYER_NAME_PATTERN.test(newName)) {
              toast.error(
                "Layer name must be at least 3 characters and contain only letters, numbers, spaces, dashes, or underscores.",
              );
              setShowRenameLayerModal?.(false);
              return;
            }
            const newLayer = JSON.parse(JSON.stringify(layer));
            newLayer.name = newName;
            renameLayer(layer.id, newName)
              .then(() => {
                setLayers(
                  [
                    newLayer,
                    ...(layers?.filter((v) => v.id !== layer.id) || []),
                  ].sort((l1, l2) => l2.id - l1.id),
                );
                setCutoutInProgress(null);
                setSelectedLayer(null);
              })
              .catch((error) => toast.error(error?.data || error?.detail));
            setShowRenameLayerModal?.(false);
          }}
        />
      ) : (
        <span className={styles.cascadeHeaderName}>{layer.name}</span>
      )}
      <button
        type="button"
        className={styles.cascadeDeleteButton}
        onClick={handleDelete}
        aria-label="Delete layer"
      >
        <DeleteIcon />
      </button>
      {addAnnotationTip ? (
        <Guide
          id={addAnnotationTip.id}
          title={addAnnotationTip.title}
          description={addAnnotationTip.description}
          open
          placement="left"
        >
          <Tooltip
            title="Add annotation to this layer"
            placement="bottom"
            arrow
          >
            <button
              type="button"
              className={styles.cascadeAddButton}
              onClick={handleAddAnnotation}
              aria-label="Add annotation on current frame"
            >
              <AddIcon fontSize="inherit" />
            </button>
          </Tooltip>
        </Guide>
      ) : (
        <Tooltip title="Add annotation to this layer" placement="bottom" arrow>
          <button
            type="button"
            className={styles.cascadeAddButton}
            onClick={handleAddAnnotation}
            aria-label="Add annotation on current frame"
          >
            <AddIcon fontSize="inherit" />
          </button>
        </Tooltip>
      )}
    </div>
  );

  if (isExpanded && hasCutouts) {
    return (
      <div
        className={cx(
          styles.cascadeContainer,
          styles.cascadeWithCutouts,
          styles.cascadeExpanded,
        )}
      >
        {header}
        <div className={styles.cascadeCutouts}>
          {sortedCutouts.map((cutout) => (
            <CutoutCard
              key={cutout.id}
              cutout={cutout}
              layer={layer}
              onActivateLayer={handleSingleClick}
            />
          ))}
        </div>
        <button
          type="button"
          className={styles.cascadeExpandTrigger}
          onClick={handleTriggerClick}
          aria-label="Collapse cutouts"
        >
          <span className={styles.cascadeExpandPill}>
            <KeyboardArrowUpIcon fontSize="inherit" />
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className={cx(styles.cascadeContainer, styles.cascadeWithCutouts)}>
      {header}
      <div className={styles.cascadeFront}>
        <Layer layer={layer} {...layerProps} wrappedByHeader />
      </div>
      {ghostCount >= 1 && (
        <div className={cx(styles.cascadeBackCard, styles.cascadeBackCard1)} />
      )}
      {ghostCount >= 2 && (
        <div className={cx(styles.cascadeBackCard, styles.cascadeBackCard2)} />
      )}
      {hasCutouts && (
        <button
          type="button"
          className={styles.cascadeExpandTrigger}
          onClick={handleTriggerClick}
          aria-label="Expand cutouts"
        >
          <span className={styles.cascadeExpandPill}>
            <KeyboardArrowDownIcon fontSize="inherit" />
          </span>
        </button>
      )}
    </div>
  );
};

export default LayerCascade;
